/**
 * @krisspy-file
 * @type page
 * @name "AdminPage"
 * @title "Admin Panel"
 * @description "Admin panel: správa globálních nápadů a schvalování návrhů od uživatelů"
 * @routes ["/admin"]
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Lightbulb,
  MessageSquare,
  Plus,
  Trash2,
  Check,
  X,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isSupabaseConfigured, supabase } from '../lib/supabase';
import {
  MOCK_GLOBAL_IDEAS,
  MOCK_SUGGESTIONS,
  MOCK_JOURNEYS,
  JourneyType,
  MockGlobalIdea,
  MockSuggestion,
} from '../lib/mockData';

type Tab = 'ideas' | 'suggestions';

// ─── Status badge helper ──────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: MockSuggestion['status'] }> = ({ status }) => {
  const cfg = {
    pending: { label: 'Čeká na schválení', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    approved: { label: 'Schváleno', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    rejected: { label: 'Zamítnuto', cls: 'bg-red-100 text-red-700 border-red-200' },
  }[status];
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

// ─── Approval modal ──────────────────────────────────────────────────────────

interface ApprovalState {
  suggestionId: string;
  title: string;
  // přednačtené z target_phase_id v suggestions
  prefilledJourney?: JourneyType;
  prefilledPhaseId?: string;
}

const ApprovalModal: React.FC<{
  state: ApprovalState;
  onClose: () => void;
  onConfirm: (params: { journey: JourneyType; phaseId: string }) => void;
  loading: boolean;
}> = ({ state, onClose, onConfirm, loading }) => {
  const [journey, setJourney] = useState<JourneyType>(state.prefilledJourney ?? 'customer');
  const [phaseId, setPhaseId] = useState<string>(
    state.prefilledPhaseId ?? MOCK_JOURNEYS.customer.phases[0].id
  );

  // Když se změní journey, reset phase na první v dané cestě
  useEffect(() => {
    const firstPhase = MOCK_JOURNEYS[journey].phases[0];
    // Pokud aktuální phaseId nepatří k vybrané journey, reset
    const phaseInJourney = MOCK_JOURNEYS[journey].phases.some(p => p.id === phaseId);
    if (!phaseInJourney) {
      setPhaseId(firstPhase.id);
    }
  }, [journey, phaseId]);

  const phases = MOCK_JOURNEYS[journey].phases;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Schválit nápad</h2>
          <p className="text-xs text-gray-500 mt-0.5 truncate">„{state.title}"</p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-800">
            <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-500" />
            <span>
              Po schválení se nápad zařadí do globální knihovny a uvidí ho noví uživatelé v zadané fázi.
            </span>
          </div>

          {/* Journey selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Cesta</label>
            <div className="grid grid-cols-3 gap-2">
              {(['customer', 'employee', 'personal'] as JourneyType[]).map(j => (
                <button
                  key={j}
                  type="button"
                  onClick={() => setJourney(j)}
                  className={`py-2 px-2 rounded-xl border-2 text-xs font-semibold transition-all
                    ${journey === j
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                    }`}
                >
                  {MOCK_JOURNEYS[j].label}
                </button>
              ))}
            </div>
          </div>

          {/* Phase selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Fáze</label>
            <select
              value={phaseId}
              onChange={e => setPhaseId(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all bg-white"
            >
              {phases.map(p => (
                <option key={p.id} value={p.id}>
                  {p.order}. {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-2.5 px-4 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-60"
            >
              Zrušit
            </button>
            <button
              type="button"
              onClick={() => onConfirm({ journey, phaseId })}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm transition-colors disabled:opacity-60"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Schvaluji...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Schválit a zařadit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── AdminPage ────────────────────────────────────────────────────────────────

const AdminPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('ideas');

  // ── Global ideas state ──
  const [ideas, setIdeas] = useState<MockGlobalIdea[]>(MOCK_GLOBAL_IDEAS);
  const [newIdeaTitle, setNewIdeaTitle] = useState('');
  const [newIdeaJourney, setNewIdeaJourney] = useState<JourneyType>('customer');
  const [newIdeaPhaseId, setNewIdeaPhaseId] = useState<string>(MOCK_JOURNEYS.customer.phases[0].id);
  const [addingIdea, setAddingIdea] = useState(false);
  const [ideasLoading, setIdeasLoading] = useState(false);

  // ── Suggestions state ──
  const [suggestions, setSuggestions] = useState<Array<MockSuggestion & { targetPhaseId?: string }>>(MOCK_SUGGESTIONS);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // ── Approval modal state ──
  const [approval, setApproval] = useState<ApprovalState | null>(null);
  const [approvalLoading, setApprovalLoading] = useState(false);

  // ── Auth guard ──
  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // ── Reset phase when journey changes in "add idea" form ──
  useEffect(() => {
    const phaseInJourney = MOCK_JOURNEYS[newIdeaJourney].phases.some(p => p.id === newIdeaPhaseId);
    if (!phaseInJourney) {
      setNewIdeaPhaseId(MOCK_JOURNEYS[newIdeaJourney].phases[0].id);
    }
  }, [newIdeaJourney, newIdeaPhaseId]);

  // ── Load from Supabase when configured ──
  useEffect(() => {
    if (!isSupabaseConfigured || !user || user.role !== 'admin') return;

    setIdeasLoading(true);
    supabase
      .from('ideas')
      .select('id, title, phase_id, created_at')
      .order('created_at', { ascending: false })
      .then(({ data }: { data: any }) => {
        if (data) {
          setIdeas(
            (data as any[]).map((r: any) => ({
              id: r.id,
              title: r.title,
              createdAt: r.created_at?.slice(0, 10) ?? '',
              phaseId: r.phase_id,
            }) as MockGlobalIdea & { phaseId?: string })
          );
        }
        setIdeasLoading(false);
      });

    setSuggestionsLoading(true);
    supabase
      .from('suggestions')
      .select('id, user_id, title, status, target_phase_id, created_at')
      .order('created_at', { ascending: false })
      .then(({ data }: { data: any }) => {
        if (data) {
          setSuggestions(
            (data as any[]).map((r: any) => ({
              id: r.id,
              userId: r.user_id,
              userEmail: r.user_id, // TODO: join s auth.users for email
              title: r.title,
              status: r.status,
              createdAt: r.created_at?.slice(0, 10) ?? '',
              targetPhaseId: r.target_phase_id ?? undefined,
            }))
          );
        }
        setSuggestionsLoading(false);
      });
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex items-center gap-2 text-gray-500">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
          Načítání...
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  // ─── Helper: zjistit do které journey patří phase ────────────────────────

  const findJourneyForPhase = (phaseId: string): JourneyType | undefined => {
    for (const j of ['customer', 'employee', 'personal'] as JourneyType[]) {
      if (MOCK_JOURNEYS[j].phases.some(p => p.id === phaseId)) return j;
    }
    return undefined;
  };

  const findPhaseLabel = (phaseId?: string): string => {
    if (!phaseId) return '—';
    for (const j of ['customer', 'employee', 'personal'] as JourneyType[]) {
      const found = MOCK_JOURNEYS[j].phases.find(p => p.id === phaseId);
      if (found) {
        return `${MOCK_JOURNEYS[j].label} → ${found.order}. ${found.name}`;
      }
    }
    return phaseId;
  };

  // ─── Idea handlers ──────────────────────────────────────────────────────────

  const handleAddIdea = async () => {
    const title = newIdeaTitle.trim();
    if (!title || !newIdeaPhaseId) return;

    if (isSupabaseConfigured) {
      const newId = `idea-admin-${Date.now()}`;
      const { data, error } = await supabase
        .from('ideas')
        .insert([{ id: newId, title, description: title, phase_id: newIdeaPhaseId, category: 'admin' }])
        .select()
        .single();
      if (!error && data) {
        setIdeas(prev => [
          {
            id: (data as any).id,
            title: (data as any).title,
            createdAt: (data as any).created_at?.slice(0, 10) ?? '',
            phaseId: (data as any).phase_id,
          } as MockGlobalIdea & { phaseId?: string },
          ...prev,
        ]);
      }
    } else {
      const newIdea: MockGlobalIdea = {
        id: `g-${Date.now()}`,
        title,
        createdAt: new Date().toISOString().slice(0, 10),
      };
      setIdeas(prev => [newIdea, ...prev]);
    }

    setNewIdeaTitle('');
    setAddingIdea(false);
  };

  const handleDeleteIdea = async (id: string) => {
    if (!window.confirm('Smazat tento globální nápad?')) return;
    if (isSupabaseConfigured) {
      await supabase.from('ideas').delete().eq('id', id);
    }
    setIdeas(prev => prev.filter(i => i.id !== id));
  };

  // ─── Suggestion handlers ────────────────────────────────────────────────────

  const openApprovalModal = (suggestion: MockSuggestion & { targetPhaseId?: string }) => {
    const prefilledJourney = suggestion.targetPhaseId
      ? findJourneyForPhase(suggestion.targetPhaseId)
      : undefined;
    setApproval({
      suggestionId: suggestion.id,
      title: suggestion.title,
      prefilledJourney,
      prefilledPhaseId: suggestion.targetPhaseId,
    });
  };

  // UUID v8 detekce - mock suggestions mají id jako "s-1", reálné UUID
  const isUuid = (id: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

  const handleApprove = async ({ journey: _journey, phaseId }: { journey: JourneyType; phaseId: string }) => {
    if (!approval) return;
    setApprovalLoading(true);

    const isMockSuggestion = !isUuid(approval.suggestionId);

    try {
      if (isSupabaseConfigured && !isMockSuggestion) {
        // 1) Insert as global idea linked to phase
        const newIdeaId = `idea-approved-${Date.now()}`;
        const { error: insertError } = await supabase.from('ideas').insert({
          id: newIdeaId,
          title: approval.title,
          description: approval.title,
          phase_id: phaseId,
          category: 'community',
        });
        if (insertError) throw insertError;

        // 2) Update suggestion status
        const { error: updateError } = await supabase
          .from('suggestions')
          .update({ status: 'approved' })
          .eq('id', approval.suggestionId);
        if (updateError) throw updateError;

        // 3) Append to local ideas state
        setIdeas(prev => [
          {
            id: newIdeaId,
            title: approval.title,
            createdAt: new Date().toISOString().slice(0, 10),
            phaseId,
          } as MockGlobalIdea & { phaseId?: string },
          ...prev,
        ]);
      } else if (isMockSuggestion) {
        // Mock suggestion - jen lokální append do ideas (in-memory demo)
        const newIdeaId = `idea-mock-${Date.now()}`;
        setIdeas(prev => [
          {
            id: newIdeaId,
            title: approval.title,
            createdAt: new Date().toISOString().slice(0, 10),
            phaseId,
          } as MockGlobalIdea & { phaseId?: string },
          ...prev,
        ]);
      }

      setSuggestions(prev =>
        prev.map(s => (s.id === approval.suggestionId ? { ...s, status: 'approved' } : s))
      );
      setApproval(null);
    } catch (err: any) {
      console.error('[AdminPage] approve failed', err);
      const msg = err?.message ?? '';
      const code = err?.code ?? '';
      let userMsg = 'Schválení se nepodařilo. Zkuste to prosím znovu.';
      if (msg.includes('permission denied')) {
        userMsg = 'Nedostatek oprávnění. Odhlaste se a znovu přihlaste, aby se obnovily admin oprávnění v session.';
      } else if (code === '23505') {
        userMsg = 'Tento nápad už ve vaší DB existuje (duplicate ID).';
      } else if (msg) {
        userMsg += `\n\nDetail: ${msg}`;
      }
      window.alert(userMsg);
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleReject = async (id: string) => {
    if (isSupabaseConfigured && isUuid(id)) {
      await supabase.from('suggestions').update({ status: 'rejected' }).eq('id', id);
    }
    setSuggestions(prev =>
      prev.map(s => (s.id === id ? { ...s, status: 'rejected' } : s))
    );
  };

  // ─── Counts ─────────────────────────────────────────────────────────────────

  const pendingCount = suggestions.filter(s => s.status === 'pending').length;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900">Admin Panel</h1>
            <p className="text-sm text-gray-400">Správa globálních nápadů a návrhů od uživatelů</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6 w-fit">
          <button
            onClick={() => setTab('ideas')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              tab === 'ideas'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Lightbulb className="w-4 h-4" />
            Globální nápady
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tab === 'ideas' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-500'}`}>
              {ideas.length}
            </span>
          </button>
          <button
            onClick={() => setTab('suggestions')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              tab === 'suggestions'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Náměty od uživatelů
            {pendingCount > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full font-bold bg-red-100 text-red-600">
                {pendingCount}
              </span>
            )}
          </button>
        </div>

        {/* ── TAB: Global Ideas ── */}
        {tab === 'ideas' && (
          <div className="space-y-4">
            {/* Add new idea */}
            {addingIdea ? (
              <div className="bg-white rounded-2xl border border-indigo-200 shadow-sm p-4 space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Název nového globálního nápadu
                </label>
                <input
                  autoFocus
                  type="text"
                  value={newIdeaTitle}
                  onChange={e => setNewIdeaTitle(e.target.value)}
                  placeholder="např. Personalizace pomocí AI"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent placeholder-gray-400"
                />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Cesta</label>
                    <select
                      value={newIdeaJourney}
                      onChange={e => setNewIdeaJourney(e.target.value as JourneyType)}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                    >
                      {(['customer', 'employee', 'personal'] as JourneyType[]).map(j => (
                        <option key={j} value={j}>{MOCK_JOURNEYS[j].label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Fáze</label>
                    <select
                      value={newIdeaPhaseId}
                      onChange={e => setNewIdeaPhaseId(e.target.value)}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                    >
                      {MOCK_JOURNEYS[newIdeaJourney].phases.map(p => (
                        <option key={p.id} value={p.id}>{p.order}. {p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleAddIdea}
                    disabled={!newIdeaTitle.trim()}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    Přidat
                  </button>
                  <button
                    onClick={() => { setAddingIdea(false); setNewIdeaTitle(''); }}
                    className="px-4 py-2 text-sm font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAddingIdea(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" />
                Přidat globální nápad
              </button>
            )}

            {/* Ideas list */}
            {ideasLoading ? (
              <div className="text-center py-10 text-gray-400 text-sm">Načítám nápady...</div>
            ) : ideas.length === 0 ? (
              <div className="text-center py-12">
                <Lightbulb className="w-10 h-10 mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-400">Zatím žádné globální nápady</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {ideas.map(idea => {
                  const ideaWithPhase = idea as MockGlobalIdea & { phaseId?: string };
                  return (
                    <div
                      key={idea.id}
                      className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4 flex items-start gap-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 leading-snug">{idea.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {ideaWithPhase.phaseId ? findPhaseLabel(ideaWithPhase.phaseId) : 'Bez fáze'} · {idea.createdAt}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteIdea(idea.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-red-500 flex-shrink-0 p-1 rounded"
                        title="Smazat"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Suggestions ── */}
        {tab === 'suggestions' && (
          <div className="space-y-3">
            {suggestionsLoading ? (
              <div className="text-center py-10 text-gray-400 text-sm">Načítám náměty...</div>
            ) : suggestions.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-10 h-10 mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-400">Žádné náměty od uživatelů</p>
              </div>
            ) : (
              <>
                {suggestions
                  .slice()
                  .sort((a, b) => {
                    const order = { pending: 0, approved: 1, rejected: 2 };
                    return order[a.status] - order[b.status];
                  })
                  .map(suggestion => (
                    <div
                      key={suggestion.id}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-800">{suggestion.title}</p>
                          <StatusBadge status={suggestion.status} />
                        </div>
                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5 flex-wrap">
                          <span>{suggestion.userEmail} · {suggestion.createdAt}</span>
                          {suggestion.targetPhaseId && (
                            <>
                              <ArrowRight className="w-3 h-3 text-gray-300" />
                              <span className="text-indigo-600 font-medium">
                                Navrženo pro: {findPhaseLabel(suggestion.targetPhaseId)}
                              </span>
                            </>
                          )}
                        </p>
                      </div>

                      {suggestion.status === 'pending' && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => openApprovalModal(suggestion)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
                          >
                            <Check className="w-3.5 h-3.5" />
                            Schválit
                          </button>
                          <button
                            onClick={() => handleReject(suggestion.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                            Zamítnout
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
              </>
            )}
          </div>
        )}

        {/* Approval modal */}
        {approval && (
          <ApprovalModal
            state={approval}
            onClose={() => setApproval(null)}
            onConfirm={handleApprove}
            loading={approvalLoading}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPage;
